import { useContext, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useIntl } from "react-intl";

import {
  BuilderContext,
  type RegisteredComponent,
} from "@builder.io/sdk-react";

import { BodyCopy } from "~/hec/components/ui/body-copy";
import { Heading } from "~/hec/components/ui/heading";
import { getGuestLearningData } from "~/hooks/learning-center/guest-learning-data";
import useLearningCenterMutation from "~/hooks/learning-center/use-learning-center-mutation";
import { useRootLayoutData } from "~/root";

import messages from "./messages";
import ModuleAccordion from "./module-accordion";
import ModuleProgress from "./module-progress";
import useLearningCenterModule from "./use-learning-center-module";

/**
 * SFCC-11505 — port of hec-composable `learning-center-module/learningCenterModule.jsx`.
 * The LMS module player (HSA Learning Center): reads the module reference from
 * the page entry's `BuilderContext.content.data.module`, drives the lesson
 * accordion + quiz + progress + next-step CTA via `useLearningCenterModule`, and
 * (FSA/HSA, server-gated) awards AnnexCloud points on level completion.
 *
 * `noWrap` block — registered as "Learning Center Module" (the name live Builder
 * content references) and spreads `attributes` on its root element.
 */
interface LearningCenterModuleProps {
  attributes?: Record<string, unknown>;
}

/** The module reference shape on the page entry (`content.data.module`). */
interface ModuleRef {
  id?: string;
  value?: { name?: string };
}

const LearningCenterModule = ({ attributes }: LearningCenterModuleProps) => {
  const builderContext = useContext(BuilderContext);
  const moduleRef = (
    builderContext?.content?.data as { module?: ModuleRef } | undefined
  )?.module;

  const { isRegistered } = useRootLayoutData();
  const isGuest = !isRegistered;
  const intl = useIntl();
  const { openGoal, startLevel, startModule } = useLearningCenterMutation();

  // Best-effort guest seed (composable parity): on every module a guest visits,
  // record the goal/level/module "start" derived from the module entry's
  // "GOAL--LEVEL--MODULE" name so the Goal Based Learning section reflects
  // progress for the started level/module — not just the very first module ever
  // opened (the old `goals.length > 0` single-fire gate). Each builder lazily
  // skips goal/level/module entries that already exist (openGoal returns early,
  // startLevel/startModule no-op on a present id), so re-seeding is idempotent.
  // The mutation layer also lazily creates these on lesson/quiz completion, so a
  // missing name here is harmless.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !isGuest) return;
    const name = moduleRef?.value?.name;
    if (!name) return;
    const parts = name.split("--");
    if (parts.length < 3) return;
    seededRef.current = true;

    const goalId = parts[0];
    const levelId = parts.slice(0, 2).join("--");
    void (async () => {
      try {
        await openGoal(goalId);
        const data = getGuestLearningData();
        // `startLevel` pushes unconditionally and mutates `data` in place (no
        // persist); only seed when the level is new (it already exists once the
        // guest has started this level before). `startModule` then persists the
        // same `data` object, so it sees the just-pushed level.
        const levelExists = data.goals
          .find(g => g.id === goalId)
          ?.levels.some(l => l.id === levelId);
        if (!levelExists) startLevel(levelId, goalId, data);
        // `startModule` already no-ops when the module id is present on the level.
        await startModule(name, levelId, data, "");
      } catch (error) {
        console.error("[LearningCenterModule] guest seed failed:", error);
      }
    })();
  }, [isGuest, moduleRef, openGoal, startLevel, startModule]);

  // One-time guest notice about the lesson limit.
  const toastedRef = useRef(false);
  useEffect(() => {
    if (isGuest && !toastedRef.current) {
      toastedRef.current = true;
      toast(intl.formatMessage(messages.guestLessonLimitNotice));
    }
  }, [isGuest, intl]);

  const moduleData = useLearningCenterModule(
    moduleRef ? { id: moduleRef.id } : undefined,
  );
  const { module, componentIsLoading, isError } = moduleData;

  if (componentIsLoading) {
    return (
      <div {...(attributes ?? {})} data-testid="lms-module-loading">
        <div
          className="flex items-center gap-3 py-4"
          role="status"
          aria-live="polite"
        >
          <span
            className="h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent text-accent"
            aria-hidden="true"
            data-testid="lms-module-loading-spinner"
          />
          <BodyCopy data-testid="lms-module-loading-text">
            {intl.formatMessage(messages.moduleLoading)}
          </BodyCopy>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div {...(attributes ?? {})} data-testid="lms-module-error">
        {intl.formatMessage(messages.loadError)}
      </div>
    );
  }

  if (!module) {
    return (
      <div {...(attributes ?? {})} data-testid="lms-module-empty">
        {intl.formatMessage(messages.selectModule)}
      </div>
    );
  }

  return (
    <div {...(attributes ?? {})} data-testid="learningCenterModule">
      <div className="mb-6 px-2 lg:mb-8 lg:px-6">
        <Heading
          tag="h3"
          variant="h3"
          className="mb-1"
          data-testid="learningCenterModule-Heading"
        >
          {module.title}
        </Heading>
        <BodyCopy
          size="large"
          className="mb-3 lg:mb-6"
          data-testid="learningCenterModule-BodyCopy"
        >
          {intl.formatMessage(messages.earnPointsInstructions)}
        </BodyCopy>
        <ModuleProgress lessons={module.lessons} quiz={module.quiz} />
        <ModuleAccordion moduleData={moduleData} />
      </div>
    </div>
  );
};

export const LearningCenterModuleDefinition = {
  component: LearningCenterModule,
  name: "Learning Center Module",
  noWrap: true,
  canHaveChildren: false,
} satisfies RegisteredComponent;

export default LearningCenterModule;
